import { normalizeLanguageCode, setAppLanguage } from '@app/i18n/helpers';
import { SUPPORTED_LANGUAGE_DETAILS, SUPPORTED_LANGUAGES, type AppLanguage } from '@app/i18n/supportedLanguages';
import { useAuthContext } from '@context/auth/useAuthContext';
import { useTranslation } from 'react-i18next';

const getSafeLanguage = (value: string | undefined): AppLanguage => {
  return normalizeLanguageCode(value);
};

const LanguageSelector = () => {
  const { i18n, t } = useTranslation();
  const { persistLocale } = useAuthContext();

  const currentLanguage = getSafeLanguage(i18n.language);
  // Only two languages are supported, so this acts as a toggle: pick the first
  // language that isn't the current one (falls back to current if alone).
  const nextLanguage = SUPPORTED_LANGUAGES.find((language) => language !== currentLanguage) ?? currentLanguage;

  const current = SUPPORTED_LANGUAGE_DETAILS[currentLanguage];
  const next = SUPPORTED_LANGUAGE_DETAILS[nextLanguage];

  const handleLanguageSwitch = async () => {
    await setAppLanguage(nextLanguage);
    persistLocale(nextLanguage);
  };

  return (
    <button
      type="button"
      onClick={() => void handleLanguageSwitch()}
      title={t('common-language-switch-aria', { language: next.label })}
      aria-label={t('common-language-switch-aria', { language: next.label })}
      className={
        'relative inline-flex h-10 w-10 items-center justify-center rounded-full ' +
        'border border-slate-200 bg-white hover:bg-slate-50 focus:outline-none ' +
        'focus:ring-2 focus:ring-gray-100 cursor-pointer'
      }
    >
      <span className="text-[18px] leading-none">{current.flag}</span>
    </button>
  );
};

export default LanguageSelector;
